import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Translate } from "../../i18n/translate";
import { directoryTree, type AccessDirectory } from "@shared/access/directory";
import type { PersonalLevelSetting } from "@shared/access/grants";
import { levelCap, type HomeKind } from "@shared/access/homeKind";
import { LEVELS, reaches, type PersonalLevel } from "@shared/access/levels";
import { samePlace, type Place } from "@shared/access/places";
import type { ResourcePath } from "@shared/access/resources";
import { isRoleName, ROLE_NAMES, type RoleName } from "@shared/access/roles";
import { isInsideHome, type HomeChoice } from "@shared/access/userAccess";
import {
  formProblems,
  nendaPlace,
  nendaPlaceLabel,
  roleTooHigh,
} from "./formChecks";
import {
  AREAS,
  childrenOf,
  describeForm,
  describeShownLevel,
  grantsFromForm,
  levelLabel,
  placeLabel,
  resourceLabel,
  resultByPlace,
  roleLabel,
  shownLevel,
  withPersonalLevel,
  type GrantForm,
} from "./grantForm";

interface PlaceOption {
  place: Place;
  label: string;
  group: string;
  outside: boolean;
}

/** Every place the caller may name for this user, Nenda first. */
export function placeOptions(
  t: Translate,
  directory: AccessDirectory,
  home: HomeChoice | null,
  kind: HomeKind,
  callerIsNendaStaff: boolean
): PlaceOption[] {
  const tree = directoryTree(directory);
  const outside = (place: Place) =>
    !home || !isInsideHome(place, home, directory, tree);
  const options: PlaceOption[] = [];
  if (kind === "nenda") {
    options.push({
      place: nendaPlace(directory),
      label: nendaPlaceLabel(t),
      group: t("users:grantEditor.groups.nenda"),
      outside: false,
    });
  }
  for (const c of directory.companies) {
    if (c.id === directory.nendaCompanyId) continue;
    const place: Place = { kind: "company", id: c.id };
    options.push({
      place,
      label: t("users:grantEditor.allPremises", { name: c.name }),
      group: t("users:grantEditor.groups.companies"),
      outside: outside(place),
    });
  }
  const companyName = (id: string | null) =>
    directory.companies.find((c) => c.id === id)?.name ??
    t("users:grantEditor.noCompany");
  const premises = [...directory.premises].sort(
    (a, b) =>
      companyName(a.companyId).localeCompare(companyName(b.companyId)) ||
      a.name.localeCompare(b.name)
  );
  for (const p of premises) {
    const place: Place = { kind: "premise", id: p.id };
    options.push({
      place,
      label: p.name,
      group: t("users:grantEditor.groups.premisesOf", {
        company: companyName(p.companyId),
      }),
      outside: outside(place),
    });
  }
  return callerIsNendaStaff ? options : options.filter((o) => !o.outside);
}

function RoleSelect({
  value,
  onChange,
  kind,
  label,
  allowNone = true,
  testId,
  disabled,
}: {
  value: RoleName | null;
  onChange: (role: RoleName | null) => void;
  kind: HomeKind;
  label?: string;
  allowNone?: boolean;
  testId: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation(["users", "common"]);
  return (
    <TextField
      select
      label={label ?? t("common:labels.role")}
      value={value ?? ""}
      onChange={(e) =>
        onChange(isRoleName(e.target.value) ? e.target.value : null)
      }
      size="small"
      disabled={disabled}
      inputProps={{ "data-testid": testId }}
    >
      {allowNone && (
        <MenuItem value="">
          <em>{t("grantEditor.noRoleOption")}</em>
        </MenuItem>
      )}
      {ROLE_NAMES.map((role) => {
        const tooHigh = roleTooHigh(role, kind);
        return (
          <MenuItem
            key={role}
            value={role}
            disabled={tooHigh && value !== role}
          >
            {tooHigh
              ? t("grantEditor.nendaOnly", { option: roleLabel(t, role) })
              : roleLabel(t, role)}
          </MenuItem>
        );
      })}
    </TextField>
  );
}

function optionFor(
  t: Translate,
  options: readonly PlaceOption[],
  place: Place,
  directory: AccessDirectory
): PlaceOption {
  return (
    options.find((o) => samePlace(o.place, place)) ?? {
      place,
      label:
        place.kind === "company" && place.id === directory.nendaCompanyId
          ? nendaPlaceLabel(t)
          : placeLabel(t, directory, place),
      group: t("users:grantEditor.groups.other"),
      outside: true,
    }
  );
}

function PlacesField({
  options,
  directory,
  value,
  onChange,
  label,
  testId,
  disabled,
}: {
  options: PlaceOption[];
  directory: AccessDirectory;
  value: readonly Place[];
  onChange: (places: Place[]) => void;
  label: string;
  testId: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation("users");
  const selected = value.map((place) =>
    optionFor(t, options, place, directory)
  );
  return (
    <Autocomplete
      multiple
      size="small"
      disabled={disabled}
      options={options}
      value={selected}
      groupBy={(o) => o.group}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(a, b) => samePlace(a.place, b.place)}
      onChange={(_e, next) => onChange(next.map((o) => o.place))}
      renderOption={(props, o) => (
        <li {...props} key={`${o.place.kind}:${o.place.id}`}>
          <Box>
            <Typography variant="body2">{o.label}</Typography>
            {o.outside && (
              <Typography variant="caption" color="warning.main">
                {t("grantEditor.outsideHome")}
              </Typography>
            )}
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          inputProps={{ ...params.inputProps, "data-testid": testId }}
        />
      )}
    />
  );
}

const INHERIT = "";

function LevelSelect({
  value,
  inheritLabel,
  onChange,
  cap,
  testId,
  disabled,
}: {
  value: PersonalLevel | null;
  inheritLabel: string;
  onChange: (level: PersonalLevel | null) => void;
  cap: PersonalLevel;
  testId: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation(["users", "common"]);
  return (
    <TextField
      select
      size="small"
      value={value ?? INHERIT}
      onChange={(e) =>
        onChange(
          e.target.value === INHERIT ? null : (e.target.value as PersonalLevel)
        )
      }
      disabled={disabled}
      sx={{ minWidth: 170 }}
      inputProps={{
        "data-testid": testId,
        "aria-label": t("common:labels.level"),
      }}
    >
      <MenuItem value={INHERIT}>
        <em>{inheritLabel}</em>
      </MenuItem>
      <MenuItem value="none">{levelLabel(t, "none")}</MenuItem>
      {LEVELS.map((level) => (
        <MenuItem
          key={level}
          value={level}
          disabled={!reaches(cap, level) && value !== level}
        >
          {reaches(cap, level)
            ? levelLabel(t, level)
            : t("grantEditor.nendaOnly", { option: levelLabel(t, level) })}
        </MenuItem>
      ))}
    </TextField>
  );
}

/**
 * One row per area with its level. Expanding a row shows the area's resources
 * with the level each inherits, and each can be set higher or lower.
 */
export function PersonalLevelsEditor({
  role,
  levels,
  onChange,
  kind,
  testIdPrefix,
  disabled,
  patientDataOnly,
}: {
  role: RoleName | null;
  levels: readonly PersonalLevelSetting[];
  onChange: (levels: PersonalLevelSetting[]) => void;
  kind: HomeKind;
  testIdPrefix: string;
  disabled?: boolean;
  /** Only the patient-data row can change. */
  patientDataOnly?: boolean;
}) {
  const { t } = useTranslation("users");
  const [open, setOpen] = useState<ReadonlySet<ResourcePath>>(new Set());
  const cap = levelCap(kind);
  const toggle = (area: ResourcePath) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(area)) next.delete(area);
      else next.add(area);
      return next;
    });

  const row = (path: ResourcePath, child: boolean) => {
    const shown = shownLevel(role, levels, path);
    const personal = levels.find((p) => p.resource === path)?.level ?? null;
    const inherited = shownLevel(
      role,
      levels.filter((p) => p.resource !== path),
      path
    );
    const children = child ? [] : childrenOf(path);
    return (
      <TableRow key={path} data-testid={`${testIdPrefix}-row-${path}`}>
        <TableCell sx={{ pl: child ? 5 : 1, width: "40%" }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {children.length > 0 ? (
              <IconButton
                size="small"
                aria-label={t("grantEditor.showResourcesUnder", {
                  area: resourceLabel(t, path),
                })}
                aria-expanded={open.has(path)}
                onClick={() => toggle(path)}
                data-testid={`${testIdPrefix}-expand-${path}`}
              >
                {open.has(path) ? (
                  <ExpandMoreIcon fontSize="small" />
                ) : (
                  <ChevronRightIcon fontSize="small" />
                )}
              </IconButton>
            ) : (
              <Box sx={{ width: 34 }} />
            )}
            <Typography variant="body2">{resourceLabel(t, path)}</Typography>
          </Stack>
        </TableCell>
        <TableCell>
          <Typography
            variant="caption"
            color="text.secondary"
            data-testid={`${testIdPrefix}-shown-${path}`}
          >
            {describeShownLevel(t, shown)}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <LevelSelect
            value={personal}
            inheritLabel={t("grantEditor.inherit", {
              from: describeShownLevel(t, inherited),
            })}
            onChange={(level) =>
              onChange(withPersonalLevel(levels, path, level))
            }
            cap={cap}
            disabled={disabled || (patientDataOnly && path !== "patient-data")}
            testId={`${testIdPrefix}-level-${path}`}
          />
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Table size="small">
      <TableBody>
        {AREAS.map((area) => (
          <Fragment key={area}>
            {row(area, false)}
            {open.has(area) && childrenOf(area).map((c) => row(c, true))}
          </Fragment>
        ))}
      </TableBody>
    </Table>
  );
}

export interface GrantEditorProps {
  form: GrantForm;
  onChange: (form: GrantForm) => void;
  directory: AccessDirectory;
  home: HomeChoice | null;
  kind: HomeKind;
  callerIsNendaStaff: boolean;
  disabled?: boolean;
  /** Everything is fixed but patient data: a user changing their own grants. */
  patientDataOnly?: boolean;
}

/**
 * A role, personal levels and a list of places; places that differ go in the
 * Exceptions section, closed by default.
 */
export function GrantEditor({
  form,
  onChange,
  directory,
  home,
  kind,
  callerIsNendaStaff,
  disabled: allDisabled,
  patientDataOnly,
}: GrantEditorProps) {
  const { t } = useTranslation(["users", "common"]);
  const disabled = allDisabled || patientDataOnly;
  const options = useMemo(
    () => placeOptions(t, directory, home, kind, callerIsNendaStaff),
    [t, directory, home, kind, callerIsNendaStaff]
  );
  const grants = grantsFromForm(form);
  const problems = formProblems(t, form, kind, directory, home);
  const resultPlaces = grants.map((g) => g.place);
  const result = resultByPlace(grants, kind, resultPlaces, directory).filter(
    (r) => r.levels.some((l) => l !== "none")
  );
  const setMain = (patch: Partial<GrantForm["main"]>) =>
    onChange({ ...form, main: { ...form.main, ...patch } });
  const setException = (
    i: number,
    patch: Partial<GrantForm["exceptions"][number]>
  ) =>
    onChange({
      ...form,
      exceptions: form.exceptions.map((e, j) =>
        j === i ? { ...e, ...patch } : e
      ),
    });
  const freePlace = options.find(
    (o) => !form.exceptions.some((e) => samePlace(e.place, o.place))
  )?.place;
  const setLevels = form.main.personalLevels.length;

  return (
    <Stack spacing={1.5} data-testid="grant-editor">
      <Alert severity="info" icon={false} data-testid="grant-summary">
        {describeForm(t, form, directory)}
      </Alert>

      <RoleSelect
        value={form.main.role}
        onChange={(role) => setMain({ role })}
        kind={kind}
        testId="grant-role"
        disabled={disabled}
      />
      <PlacesField
        options={options}
        directory={directory}
        value={form.main.places}
        onChange={(places) => setMain({ places })}
        label={t("common:labels.places")}
        testId="grant-places"
        disabled={disabled}
      />

      <Accordion disableGutters variant="outlined">
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          data-testid="grant-personal-levels"
        >
          <Typography variant="body2">
            {setLevels > 0
              ? t("grantEditor.personalLevelsSet", { count: setLevels })
              : t("grantEditor.personalLevels")}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <PersonalLevelsEditor
            role={form.main.role}
            levels={form.main.personalLevels}
            onChange={(personalLevels) => setMain({ personalLevels })}
            kind={kind}
            testIdPrefix="main"
            disabled={allDisabled}
            patientDataOnly={patientDataOnly}
          />
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters variant="outlined" defaultExpanded={false}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          data-testid="grant-exceptions"
        >
          <Typography variant="body2">
            {form.exceptions.length > 0
              ? t("grantEditor.exceptionsCount", {
                  count: form.exceptions.length,
                })
              : t("common:labels.exceptions")}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={1.5}>
            <Typography variant="caption" color="text.secondary">
              {t("grantEditor.exceptionsHelp")}
            </Typography>
            {form.exceptions.map((e, i) => (
              <Card
                key={`${e.place.kind}:${e.place.id}`}
                variant="outlined"
                sx={{ p: 1.5 }}
                data-testid={`grant-exception-${i}`}
              >
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      select
                      size="small"
                      label={t("common:labels.place")}
                      value={`${e.place.kind}:${e.place.id}`}
                      onChange={(ev) => {
                        const next = options.find(
                          (o) =>
                            `${o.place.kind}:${o.place.id}` === ev.target.value
                        );
                        if (next) setException(i, { place: next.place });
                      }}
                      sx={{ flex: 1 }}
                      disabled={disabled}
                      inputProps={{
                        "data-testid": `grant-exception-place-${i}`,
                      }}
                    >
                      {[
                        ...(options.some((o) => samePlace(o.place, e.place))
                          ? []
                          : [optionFor(t, options, e.place, directory)]),
                        ...options.filter(
                          (o) =>
                            samePlace(o.place, e.place) ||
                            !form.exceptions.some((x) =>
                              samePlace(x.place, o.place)
                            )
                        ),
                      ].map((o) => (
                        <MenuItem
                          key={`${o.place.kind}:${o.place.id}`}
                          value={`${o.place.kind}:${o.place.id}`}
                        >
                          {o.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <IconButton
                      aria-label={t("grantEditor.removeException", {
                        place: placeLabel(t, directory, e.place),
                      })}
                      onClick={() =>
                        onChange({
                          ...form,
                          exceptions: form.exceptions.filter((_, j) => j !== i),
                        })
                      }
                      disabled={disabled}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                  <RoleSelect
                    value={e.role}
                    onChange={(role) => setException(i, { role })}
                    kind={kind}
                    testId={`grant-exception-role-${i}`}
                    disabled={disabled}
                  />
                  <PersonalLevelsEditor
                    role={e.role}
                    levels={e.personalLevels}
                    onChange={(personalLevels) =>
                      setException(i, { personalLevels })
                    }
                    kind={kind}
                    testIdPrefix={`exception-${i}`}
                    disabled={allDisabled}
                    patientDataOnly={patientDataOnly}
                  />
                </Stack>
              </Card>
            ))}
            <Box>
              <Button
                size="small"
                startIcon={<AddIcon />}
                disabled={disabled || !freePlace}
                onClick={() =>
                  freePlace &&
                  onChange({
                    ...form,
                    exceptions: [
                      ...form.exceptions,
                      { place: freePlace, role: null, personalLevels: [] },
                    ],
                  })
                }
                data-testid="grant-exception-add"
              >
                {t("grantEditor.addException")}
              </Button>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {problems.length > 0 && (
        <Alert severity="error" data-testid="grant-problems">
          {problems.map((p) => (
            <div key={p}>{p}</div>
          ))}
        </Alert>
      )}

      {resultPlaces.length > 0 && (
        <Accordion disableGutters variant="outlined" defaultExpanded={false}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            data-testid="grant-result"
          >
            <Typography variant="body2">
              {t("grantEditor.resultPerPlace")}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0, overflowX: "auto" }}>
            <Table size="small" data-testid="grant-result-table">
              <TableHead>
                <TableRow>
                  <TableCell>{t("common:labels.resource")}</TableCell>
                  {resultPlaces.map((p) => (
                    <TableCell key={`${p.kind}:${p.id}`}>
                      {placeLabel(t, directory, p)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {result.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={resultPlaces.length + 1}>
                      <Typography variant="caption" color="text.secondary">
                        {t("grantEditor.noAccessAnywhere")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {result.map((r) => (
                  <TableRow key={r.resource}>
                    <TableCell>{resourceLabel(t, r.resource)}</TableCell>
                    {r.levels.map((level, i) => (
                      <TableCell key={i}>{levelLabel(t, level)}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      )}
    </Stack>
  );
}

export { RoleSelect, PlacesField };
export type { PlaceOption };
